import { useTranslation } from 'react-i18next';
import { usePresence } from '../hooks/usePresence';
import { ChatAvatar } from './ChatAvatar';
import { BaseModal } from '@/components/modal';

interface UserProfileSheetProps {
  user: { id: string; name: string; avatar?: string | null; role?: string } | null;
  isOpen: boolean;
  onClose: () => void;
  onSendMessage?: () => void;
  onViewProfile?: (userId: string) => void;
}

export default function UserProfileSheet({
  user,
  isOpen,
  onClose,
  onSendMessage,
  onViewProfile,
}: UserProfileSheetProps) {
  const { t } = useTranslation();
  const presence = usePresence(user?.id ?? null);

  const isOnline = presence.isOnline;
  const lastSeen = presence.lastSeenFormatted;

  const getRoleBadgeColor = (role?: string) => {
    switch (role?.toLowerCase()) {
      case 'super_admin':
        return 'bg-slate-800 border-slate-800';
      case 'admin':
        return 'bg-slate-600 border-slate-600';
      case 'manager':
        return 'bg-slate-500 border-slate-500';
      case 'therapist':
        return 'bg-slate-400 border-slate-400';
      default:
        return 'bg-neutral-400 border-neutral-400';
    }
  };

  return (
    <BaseModal
      isOpen={isOpen && !!user}
      onClose={onClose}
      mobileVariant="bottomsheet"
      maxWidth="max-w-sm"
      ariaLabel={t('messaging.profile.title')}
    >
      {user && (
        <>
          {/* Drag Handle */}
          <div className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing">
            <div className="w-12 h-1 bg-white/30 rounded-full" />
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Avatar & Name */}
            <div className="flex flex-col items-center gap-4">
              {/* Avatar */}
              <div className="relative">
                <ChatAvatar
                  src={user.avatar}
                  name={user.name}
                  size="h-20 w-20"
                  borderColor="border-white/20"
                  fallbackClass="bg-slate-200 text-slate-700"
                  textSize="text-2xl"
                />

                {/* Online Status Indicator */}
                {isOnline && (
                  <div
                    className="absolute -bottom-1 -right-1 w-6 h-6 bg-success-500 border border-white/50 rounded-full"
                    title={t('messaging.profile.online')}
                  />
                )}
              </div>

              {/* Name */}
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-semibold text-neutral-800">{user.name}</h2>

                {/* Role Badge */}
                {user.role && (
                  <div className="flex justify-center">
                    <span
                      className={`px-3 py-1 rounded-lg border text-white font-medium text-sm ${getRoleBadgeColor(
                        user.role
                      )}`}
                    >
                      {t(`roles.${user.role.toLowerCase()}`)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="p-4 bg-white/20 border border-white/20 rounded-xl">
              <div className="flex items-center gap-3">
                <div
                  className={`w-3 h-3 rounded-full border border-white/20 ${
                    isOnline ? 'bg-success-500' : 'bg-neutral-400'
                  }`}
                />
                <div>
                  <p className="font-medium text-neutral-800">
                    {isOnline
                      ? t('messaging.profile.online')
                      : t('messaging.profile.offline')}
                  </p>
                  {!isOnline && lastSeen && (
                    <p className="text-xs text-neutral-500">
                      {t('messaging.profile.lastSeen', { time: lastSeen })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Email (if available in user data) */}
            {(user as any).email && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-neutral-500">
                  {t('messaging.profile.email')}
                </p>
                <p className="font-medium text-neutral-800">{(user as any).email}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3 pt-2">
              {/* Send Message */}
              {onSendMessage && (
                <button
                  onClick={() => {
                    onSendMessage();
                    onClose();
                  }}
                  className="w-full h-12 rounded-xl bg-slate-800 border border-slate-800 font-medium text-white transition-all active:scale-[0.98] flex items-center justify-center gap-3"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M2 10L18 2L10 18L8 11L2 10Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinejoin="miter"
                    />
                  </svg>
                  {t('messaging.profile.sendMessage')}
                </button>
              )}

              {/* View Profile */}
              <button
                onClick={() => {
                  onClose();
                  onViewProfile?.(user.id);
                }}
                className="w-full h-12 rounded-xl bg-white/30 border border-white/20 font-medium text-neutral-800 hover:bg-white/40 transition-colors flex items-center justify-center gap-3"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="2" />
                  <path
                    d="M4 17C4 14 6.5 12 10 12C13.5 12 16 14 16 17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="square"
                  />
                </svg>
                {t('messaging.profile.viewProfile')}
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="w-full h-12 rounded-xl bg-white/20 border border-white/20 font-medium text-neutral-800 hover:bg-white/30 transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </>
      )}
    </BaseModal>
  );
}
